import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { DEFAULT_BOM, carrying, computeBom, hipHeight, legLength, ownMass } from '@/domain/hs1'
import { axes, mechanismState, mechanisms, motorCount, rigFromModel } from '@/domain/rig'
import { Card } from '@/components/ui'
import { useSimulation } from '@/sim/SimulationProvider'
import { PageHead, num } from '@/sim/parts'
import './overview.css'

/**
 * Übersicht — was die Maschine ist, in Zahlen.
 *
 * Alles hier ist gerechnet und nichts gepflegt. Wer eine Achse anlegt, einen
 * Gelenkpunkt verschiebt oder eine Achse ausschaltet, sieht die Zahlen mitgehen.
 *
 * Die Kacheln sind nach Fragen gruppiert, nicht nach Rechenwegen: Aufbau,
 * Bewegung, Energie, Masse. Eine Zahl steht dort, wo man sie sucht.
 *
 * KEIN GELD AUF DIESER SEITE. Die Kosten stehen in der Stückliste, wo sie
 * herkommen und wo man sie auch ändern kann. Hier standen sie als zweite
 * Anzeige derselben Zahlen — und eine Übersicht, die man mitliest, wenn jemand
 * über die Schulter schaut, ist der falsche Ort dafür.
 */

interface TileProps {
  label: string
  value: string | number
  unit?: string
  note?: string
  to?: string
  warn?: boolean
}

function Tile({ label, value, unit, note, to, warn }: TileProps) {
  const body = (
    <>
      <span className="tile__label">{label}</span>
      <span className={`tile__value numeric${warn ? ' tile__value--warn' : ''}`}>
        {value}
        {unit && <span className="tile__unit">{unit}</span>}
      </span>
      {note && <span className="tile__note">{note}</span>}
    </>
  )
  /* Eine Kachel, die zu einer Seite führt, ist ein Verweis — und sieht auch so
     aus. Eine ohne Ziel ist eine Anzeige und nimmt keine Klicks an. */
  return to ? (
    <Link className="tile tile--link" to={to}>
      {body}
    </Link>
  ) : (
    <div className="tile">{body}</div>
  )
}

export function Overview() {
  const { model, state, simulation } = useSimulation()
  const { metrics, mass, drives } = simulation

  const rig = useMemo(() => rigFromModel(model, state.off), [model, state.off])
  const axisList = useMemo(() => axes(rig), [rig])
  const mechs = useMemo(() => mechanisms(rig), [rig])
  const known = useMemo(() => mechanismState(rig), [rig])

  const bom = useMemo(
    () =>
      computeBom(
        model,
        drives.map((d) => d.demand),
        state,
        DEFAULT_BOM,
        simulation.gait.cycleTime,
        simulation.power.net,
        {},
      ),
    [model, drives, state, simulation.gait.cycleTime, simulation.power.net],
  )

  const joints = rig.nodes.filter((n) => n.kind === 'gelenk')
  const active = axisList.filter((a) => a.off === null).length
  const parallel = mechs.filter((m) => m.kind === 'parallel' || m.kind === 'kurbelschwinge').length

  /* Der schwerste Antrieb je Achse — die Zahl, an der die Massenspirale hängt. */
  const worstUse = metrics.worstMomentUse

  return (
    <div className="stack">
      <PageHead
        title="Übersicht"
      />

      <Card title="Aufbau">
        <div className="tiles">
          <Tile
            label="Knickpunkte"
            value={rig.nodes.length}
            note={`${joints.length} mit Freiheit`}
            to="/parameter"
          />
          <Tile
            label="Achsen"
            value={active}
            note={
              active === axisList.length
                ? 'beide Körperseiten gezählt'
                : `${axisList.length - active} ausgeschaltet`
            }
            to="/parameter"
          />
          <Tile
            label="Motoren"
            value={motorCount(rig)}
            note="Knie und Rumpf mit je zwei"
            to="/antriebe"
          />
          <Tile
            label="Baugruppen"
            value={mechs.length}
            note={`${known.assumed} noch angenommen`}
            warn={known.assumed > 0}
            to="/antriebe"
          />
          <Tile label="Gekoppelte Antriebe" value={parallel} note="Kurbel oder Parallelkinematik" />
          <Tile
            label="Körperhöhe"
            value={num(1000 * hipHeight(model) + 1000 * 0.87, 0)}
            unit="mm"
            note={`Bein ${num(1000 * legLength(model), 0)} mm`}
            to="/parameter"
          />
        </div>
      </Card>

      <Card title="Bewegung" eyebrow={`Gangart ${state.gait}`}>
        <div className="tiles">
          <Tile label="Geschwindigkeit" value={num(metrics.speed, 2)} unit="m/s" to="/simulation" />
          <Tile label="Doppelschritt" value={num(metrics.stride, 2)} unit="m" />
          <Tile
            label="Kadenz"
            value={num(metrics.cadence, 0)}
            unit="/min"
            note={`Zyklus ${num(metrics.cycleTime, 2)} s`}
          />
          <Tile
            label="Standphase"
            value={num(100 * metrics.stance, 0)}
            unit="%"
            note={metrics.flight > 0 ? `Flug ${num(1000 * metrics.flight, 0)} ms` : 'ohne Flugphase'}
          />
          <Tile
            label="Bodenkraft Spitze"
            value={num(metrics.groundPeak, 2)}
            unit="KG"
            note={`${num(metrics.groundPeak * metrics.mass * 9.81, 0)} N`}
            to="/simulation"
          />
          <Tile
            label="Grösste Ausnutzung"
            value={num(100 * worstUse, 0)}
            unit="%"
            note="Spitzenmoment gegen die Winkelfreiheit"
            warn={worstUse > 0.9}
            to="/antriebe"
          />
        </div>
      </Card>

      <Card title="Energie">
        <div className="tiles">
          <Tile
            label="Systemleistung"
            value={num(metrics.netPower, 0)}
            unit="W"
            note="Mittel über den Zyklus"
            to="/analyse"
          />
          <Tile
            label="Spitzenleistung"
            value={num(bom.peakPower, 0)}
            unit="W"
            note="am Zwischenkreis"
          />
          <Tile
            label="Rückgewinnung"
            value={num(100 * metrics.recovered, 0)}
            unit="%"
            note="Feder und Zwischenkreis"
            to="/parameter"
          />
          <Tile
            label="Akku"
            value={num(bom.batteryEnergy, 0)}
            unit="Wh"
            note={`${num(bom.batteryMass, 1)} kg`}
            to="/stueckliste"
          />
          <Tile
            label="Reichweite"
            value={num((bom.batteryEnergy * 3600) / Math.max(1, metrics.netPower) / 3600, 1)}
            unit="h"
            note={`${num((bom.batteryEnergy * 3600 * metrics.speed) / Math.max(1, metrics.netPower) / 1000, 1)} km bei dieser Gangart`}
          />
          <Tile
            label="Transportkosten"
            value={num(metrics.transportCost, 2)}
            note="J je kg und m, auf g bezogen"
          />
        </div>
      </Card>

      <Card title="Masse">
        <div className="tiles">
          <Tile
            label="Gesamtmasse"
            value={num(metrics.mass, 1)}
            unit="kg"
            note={
              carrying(state) ? `davon ${num(state.load, 0)} kg Zuladung` : 'ohne Zuladung'
            }
            to="/stueckliste"
          />
          <Tile
            label="Eigenmasse"
            value={num(ownMass(model), 1)}
            unit="kg"
            note={
              mass.delta === null
                ? 'kein Vergleichswert'
                : `${mass.delta > 0 ? '+' : ''}${num(mass.delta, 1)} kg gegen ${num(mass.target ?? 0, 1)}`
            }
            warn={mass.relative !== null && Math.abs(mass.relative) > 0.05}
          />
          <Tile
            label="Antriebe"
            value={num(bom.actuatorMass, 1)}
            unit="kg"
            note={`${num((100 * bom.actuatorMass) / Math.max(0.001, bom.total), 0)} % der Stückliste`}
          />
          <Tile label="Struktur" value={num(bom.structureMass, 1)} unit="kg" />
          <Tile
            label="Elektronik und Sensorik"
            value={num(bom.electronicsMass + bom.sensorMass, 1)}
            unit="kg"
          />
          <Tile
            label="Stückliste gesamt"
            value={num(bom.total, 1)}
            unit="kg"
            to="/stueckliste"
          />
        </div>
      </Card>

      <Card title="Herkunft der Zahlen">
        <p className="page-lead">
          Achsen, Motoren und Baugruppen kommen aus dem Achsbaum, alles Übrige aus einem
          Durchlauf des Rechenkerns mit der eingestellten Gangart. Die Stückliste rechnet mit
          ihren Vorbelegungen; wer dort Werte übersteuert, sieht das auf der Seite Stückliste
          und nicht hier.
        </p>
        <p className="page-lead">
          {known.assumed} von {mechs.length} Baugruppen sind noch angenommen — ein Motor je
          Freiheit, direkt. Bis sie ausgearbeitet sind, ist die Motorzahl eine untere Schranke
          und die Antriebsmasse eine Schätzung.
        </p>
      </Card>
    </div>
  )
}
